import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import merge from 'lodash.merge';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Theme } from '@material-ui/core/styles/createTheme';

import { useDevelopmentStyles } from 'components/Bucket/styles'; //
import { sqlDateToLabel, formatAmountValue, isoDateTimeToLabel, formatNumber } from 'helpers/helpers'; //, compareISODates
import {
  assembleHistoricalEvents,
  HistoricalEvent,
  offsetLabel,
  devGraphLineColor,
} from 'pages/Accounts/Edit/Plan/Chart/helpers';
import { AmountValue } from 'common/interfaces';
import { ChartPeriods, chartPeriods } from 'common/interfaces';
import ChartRange from 'pages/Accounts/Edit/Plan/Chart/ChartRange';
import { EventType } from 'pages/Accounts/Edit/Plan/Chart/model';
import { DevelopmentStore, BucketTypes } from 'stores/account/developmentStore';
import FilterMenu from 'components/FilterMenu/FilterMenu';
import { Currency } from 'enums';
import { periods } from 'pages/Accounts/Detail/Development/Development';
import DevelopmentFilter from 'pages/Accounts/Detail/Development/components/DevelopmentFilter';
import TextSwitch from 'components/FormFields/TextSwitch';

const xAxisLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
  const value = `${this.value}`;
  return `${sqlDateToLabel(value, 'dd.MM.yyyy')}`;
};

const defaultFilter = [EventType.DEPOSIT, EventType.TARGET, EventType.WITHDRAWAL];

interface ValueData {
  value: AmountValue;
  date: string;
}
interface PercentageData {
  value: number;
  date: string;
}

interface PortfolioInvestments {
  CASH?: ValueData[];
  K1?: ValueData[];
  K2?: ValueData[];
  K3?: ValueData[];
  K4?: ValueData[];
  total?: ValueData[];
}

interface PortfolioHistoryValues {
  CASH?: ValueData[];
  K1?: ValueData[];
  K2?: ValueData[];
  K3?: ValueData[];
  K4?: ValueData[];
  total?: ValueData[];
}

interface PortfolioHistoryPercentages {
  CASH?: PercentageData[];
  K1?: PercentageData[];
  K2?: PercentageData[];
  K3?: PercentageData[];
  K4?: PercentageData[];
  total?: PercentageData[];
}

export interface DevelopmentGraphData {
  withHistory?: boolean;
  yearlyProfit?: { K1: number; K2: number; K3: number; CASH: number; total: number };
  investments: PortfolioInvestments;
  percentages: PortfolioHistoryPercentages;
  values: PortfolioHistoryValues;
  targets: { total: HistoricalEvent[] };
  withdrawals: { total: HistoricalEvent[] };
  singleDeposits: { total: HistoricalEvent[] };
  periodicDeposits: { total: HistoricalEvent[] };
}

interface Props {
  data: DevelopmentGraphData;
  settings?: Highcharts.Options;
  currency: Currency;
  portfolio: 'total' | 'CASH' | 'K1' | 'K2' | 'K3';
  handleCustomPeriod?: () => void;
  store: DevelopmentStore;
}

const DevelopmentLineGraph: React.FC<Props> = ({ data, settings, currency, portfolio, handleCustomPeriod, store }) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();

  const devClasses = useDevelopmentStyles();
  const tablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'), { noSsr: true });
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });

  const chartRef = useRef<Highcharts.Chart | null>();
  const domRef = useRef<HTMLDivElement | null>(null);
  const currentPortfolio = data.investments[portfolio];
  const transfersData = useMemo(
    () => (currentPortfolio ? currentPortfolio.map((dp: ValueData) => [dp.date, dp.value.value]) : []),
    [currentPortfolio],
  );
  const maxRange = useMemo(() => Math.max(transfersData.length - 1, 0), [transfersData]);

  const [period, setPeriod] = useState(store ? store.period : ChartPeriods.ALL_TIME);
  const [chart, setChart] = useState<Highcharts.Chart | undefined | null>(undefined);
  const [svgPosX, setSvgPosX] = useState(0);
  const [svgWidth, setSvgWidth] = useState(0);
  const [range, setRange] = useState([0, transfersData.length - 1]);
  const [cWidth, setCWidth] = useState(0);
  const [filter, setFilter] = useState<EventType[]>(defaultFilter);
  const [currentProfit, setCurrentProfit] = useState<number | undefined>(undefined);
  const [percentage, setPercentage] = useState(false);

  const changePeriod = (period: ChartPeriods) => {
    setPeriod(period);
    store && store.setPeriod(period);
  };

  useEffect(() => {
    setChart(chartRef.current);
  }, [setChart]);

  const events = useMemo(
    () =>
      assembleHistoricalEvents(
        data.targets.total || [],
        data.withdrawals.total || [],
        data.singleDeposits.total || [],
        data.periodicDeposits.total || [],
        percentage ? data.percentages.total! : data.investments.total!,
        currency,
        '_hover',
        false,
        filter,
        false,
      ),
    [
      filter,
      percentage,
      currency,
      data.investments.total,
      data.percentages.total,
      data.periodicDeposits.total,
      data.singleDeposits.total,
      data.targets.total,
      data.withdrawals.total,
    ],
  );

  /* FIXME: event line height if event set to min (overflows chart) */
  const setupLabels = useCallback(
    (visible: boolean, useShiftedTotals: boolean) => {
      const { current: chart } = chartRef;
      const yMin = chart?.yAxis[0].min || 0;
      const yMax = chart?.yAxis[0].max || 1;
      const xMax = chart?.xAxis[0].max || 1;
      const chartAreaWidth = chart?.plotWidth || 1;
      const chartAreaHeight = chart?.plotHeight || 1;
      const chartStartX = chart?.plotLeft || 0;
      const chartStartY = chart?.plotTop || 0;
      const { current: dom } = domRef;
      const labels = dom ? Array.from(dom.getElementsByClassName('highcharts-plot-line-label')) : [];
      const plotLines = dom ? Array.from(dom.getElementsByClassName('custom-plot-line')) : [];

      const tickPx = chartAreaWidth / (xMax || 1);
      const chartSpacingTop = chart?.options?.chart?.spacingTop || 0;

      let prevSize: number[] = [],
        prevShift: number[] = [],
        prevPos: number[] = [];

      for (let i = 0; i < labels.length; i++) {
        const c = offsetLabel(
          labels[i],
          plotLines[i],
          true,
          yMin,
          yMax,
          chartAreaHeight,
          chartSpacingTop,
          chartStartX,
          chartStartY,
          useShiftedTotals && chart ? chart.series[0].data.map((p) => p.y || 0) : [],
          tickPx,
          prevSize,
          prevShift,
          prevPos,
        );
        if (dom && visible) {
          c.addEventListener('mouseenter', () => {
            dom.setAttribute('class', 'noTooltip');
          });
          c.addEventListener('mouseleave', () => {
            dom.setAttribute('class', '');
          });
        }
      }

      setSvgPosX(chartStartX);
      setSvgWidth(chartAreaWidth);
    },
    [domRef],
  );

  const setupAxis = useCallback(() => {
    const { current: chart } = chartRef;
    if (chart) {
      const min = chart.xAxis[0].min || 0;
      const max = chart.xAxis[0].max || 0;
      const chartWidth = chart.chartWidth;
      let interval = 8;
      if (chartWidth <= 740) interval = 5;
      if (chartWidth <= 640) interval = 4;
      if (chartWidth <= 544) interval = 3;
      if (chartWidth <= 320) interval = 2;
      chart.xAxis[0].update({ tickInterval: Math.floor((max - min) / interval) });
    }
  }, []);

  useEffect(() => {
    setupAxis();
  }, [cWidth, setupAxis]);

  const getTooltip = (tooltipData: Highcharts.TooltipFormatterContextObject, percentage: boolean): string => {
    if (!tooltipData.points) {
      return '';
    }

    const header = `<div style="text-align: center; margin-bottom: 5px;">
      <strong>
      ${sqlDateToLabel(tooltipData.points[0].key as unknown as string, 'dd. MM. yyyy')}
        </strong>
      </div>`;

    const rows = tooltipData.points
      .map(
        (point) =>
          `<tr>
          <td><span style="color: ${point.color}" class="_dot">\u25CF</span></td>
          <td>${point.series.name}: </td>
          <td>${Number((point.y as number).toFixed(2)).toLocaleString(language)} ${percentage ? '%' : currency}</td>
        </tr>`,
      )
      .join('');

    return `${header}<table>${rows}</table>`;
  };

  const portfolioData = useMemo(() => {
    if (percentage) {
      return data.percentages[portfolio]?.map((dp: PercentageData) => [dp.date, dp.value * 100]) || [];
    } else {
      return data.values[portfolio]!.map((dp: ValueData) => [dp.date, dp.value.value]);
    }
  }, [data, percentage, portfolio]);

  useEffect(() => {
    if (period !== ChartPeriods.CUSTOM) {
      const lastValue = portfolioData[portfolioData.length - 1];
      const start = chartPeriods.find((p) => p.value === period)?.start || null;
      const shift = chartPeriods.find((p) => p.value === period)?.shift || null;
      const minDate = shift
        ? DateTime.fromISO(lastValue[0] as string)
            .minus(shift)
            .toMillis()
        : start || 0;
      const minValue = portfolioData.findIndex((value) => DateTime.fromISO(value[0] as string).toMillis() >= minDate);
      setRange([Math.max(minValue, 0), portfolioData.length - 1]);
    }
  }, [period, portfolioData]);

  const lineColor = devGraphLineColor(portfolio);

  const seriesData = useMemo(() => {
    const convertedData = JSON.parse(JSON.stringify(portfolioData));
    if (percentage) {
      const add = (convertedData[range[0]][1] as number) * -1;
      if (add !== 0) {
        convertedData[Math.max(range[0] - 1, 0)][1] = 0;
        for (let i = 0; i < convertedData.length - range[0]; i++) {
          (convertedData[range[0] + i][1] as number) += add;
        }
      }
    }

    return percentage
      ? [
          {
            id: 'portfolioPercentage',
            data: convertedData,
            color: lineColor,
            name: t(`accountDetail_graph_portfolio${store.percentage ? 'Percentage' : 'Values'}`),
            type: 'area',
          },
        ]
      : [
          {
            id: 'transfers',
            data: transfersData,
            color: 'rgba(120,120,120,0.8)',
            name: t('accountDetail_graph_invested'),
            type: 'area',
            fillOpacity: 1,
            fillColor: '#f5f5f5',
          },
          {
            id: 'portfolioValues',
            data: convertedData,
            color: lineColor,
            name: t(`accountDetail_graph_portfolio${store.percentage ? 'Percentage' : 'Values'}`),
            type: 'area',
          },
        ];
  }, [percentage, portfolioData, transfersData, range, lineColor, store.percentage, t]);

  const rangeLabels = useMemo(() => {
    const interval = Math.floor((data.values['total']?.length || 0) / 8);
    return data.values['total']!.filter((d, i) => i % interval === 0 && i < data.values['total']!.length - 1).map(
      (d, i) => ({
        value: i * interval,
        label: isoDateTimeToLabel(d.date, 'MM/yyyy'),
      }),
    );
  }, [data.values]);

  useEffect(() => {
    if (chart) {
      chart.xAxis[0].setExtremes(range[0], range[1]);
      const values: number[] = [];
      if (chart.series[0]) {
        chart.series[0].points.forEach((point, i) => {
          point.y !== undefined && point.y !== null && values.push(point.y);
        });
        percentage && setCurrentProfit(chart.series[0].points[chart.series[0].points.length - 1].y);
        // FIXME: check last value in custom range
      }
      chart.series[1] &&
        chart.series[1].points.forEach((point) => {
          point.y !== undefined && point.y !== null && values.push(point.y);
        });
      if (values.length) {
        const min = Math.min(...values);
        const max = Math.max(...values);
        values.length && chart.yAxis[0].setExtremes(min, max + (max - min) * 0.1);
      }
    }
  }, [chart, percentage, range, store.activeBucket]);

  const defaultSettings: Highcharts.Options = {
    title: { text: `` },
    chart: {
      type: 'area',
      spacingRight: 40,
      marginRight: 40,
      events: {
        redraw: function (this) {
          const shifted = this.series[0].userOptions.id === 'portfolioPercentage';
          const width = this.chartWidth;
          setCWidth(width);
          setupLabels(true, shifted);
        },
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      labels: {
        enabled: true,
        formatter: xAxisLabelFormatter,
        overflow: 'justify',
        reserveSpace: true,
      },
      type: 'category',
      plotLines: events,
      events: {
        afterSetExtremes: function (this) {
          setupAxis();
        },
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
          return percentage ? `${this.value} %` : `${this.value.toLocaleString(language)} ${t('currency_' + currency)}`;
        },
      },
    },
    tooltip: {
      useHTML: true,
      outside: false,
      shared: true,
      className: '_tooltip',

      formatter: function () {
        let text = '';
        text += getTooltip(this, percentage);
        return text;
      },
    },
    plotOptions: {
      area: {
        lineWidth: 2.5,
        fillOpacity: 0,
        marker: {
          symbol: 'circle',
          radius: 8,
          enabled: false,
        },
      },
    },
    series: seriesData as any,
    legend: {
      align: 'right',
      layout: 'horizontal',
      verticalAlign: 'bottom',
      floating: false,
      symbolWidth: 16,
      symbolHeight: 8,
      symbolRadius: 0,
      squareSymbol: false,
      useHTML: true,
      itemStyle: { lineHeight: '17px' },
      itemMarginTop: 12,
    },

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 740,
          },
          chartOptions: {
            yAxis: {
              labels: {
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
                  return percentage ? `${this.value} %` : `${formatAmountValue(this.value, currency, false, true)}`;
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 544,
          },
          chartOptions: {
            legend: {
              align: 'center',
              itemMarginBottom: 10,
            },
          },
        },
        {
          condition: {
            callback: () => mobile,
          },
          chartOptions: {
            chart: {
              spacingRight: 16,
              marginRight: 16,
              spacingLeft: 0,
            },
          },
        },
      ],
    },
  };

  Highcharts.setOptions({
    lang: {
      numericSymbols: [' tis.', ' mil.', ' mld.'],
    },
  });

  const graphSettings = settings ? merge({}, defaultSettings, settings) : defaultSettings;

  const handleRangeChangeInChart = (newValue: number | number[]) => {
    setRange([(newValue as number[])[0], (newValue as number[])[1]]);
    // FIXME: maybe not needed
    period !== ChartPeriods.CUSTOM && store.setPeriod(ChartPeriods.CUSTOM);
    setPeriod(ChartPeriods.CUSTOM);
  };

  const handlePercentageSwitch = () => {
    setPercentage(!percentage);
  };

  return (
    <>
      <Grid
        item
        xs={12}
        md={9}
        lg={9}
        xl={10}
        className={`${devClasses.graph} ${store.activeBucket === 0 ? '' : devClasses.hideEvents}`}
      >
        <div ref={domRef}>
          <HighchartsReact
            highcharts={Highcharts}
            options={graphSettings}
            callback={(chart: any) => {
              chartRef.current = chart;
            }}
          />
        </div>
        {tablet && (
          <div className={devClasses.filter} style={{ paddingLeft: `${svgPosX}px` }}>
            <DevelopmentFilter initial={defaultFilter} handleSetFilter={setFilter} />
          </div>
        )}
        <ChartRange
          presetRange={range}
          maxRange={maxRange}
          handleRangeChangeInChart={handleRangeChangeInChart}
          svgPosX={svgPosX}
          svgWidth={svgWidth}
          domRef={domRef.current}
          values={
            percentage
              ? data.percentages[portfolio]!.map((d) => d.value)
              : data.values[portfolio]!.map((d) => d.value.value)
          }
          dates={data.values['total']!.map((d) => d.date)}
          labels={rangeLabels}
          styling={{ margin: 0, lineColor, activeAreaRGB: '200, 200, 200', sliderColor: '#666' }}
        />

        <div
          style={{
            paddingLeft: mobile ? `${svgPosX}px` : `${svgPosX}px`,
            paddingRight: mobile ? `16px` : tablet ? '8px' : undefined,
          }}
        >
          {/* TODO: FilterMenu items hover style */}
          <FilterMenu
            onClick={changePeriod}
            items={periods}
            currentValue={period}
            selectText={period === ChartPeriods.CUSTOM ? t('accountDetail_graph_custom') : undefined}
          />
        </div>
      </Grid>

      <Grid
        container
        item
        xs={12}
        md={3}
        lg={3}
        xl={2}
        className={devClasses.filter}
        style={{
          paddingLeft: mobile ? `${svgPosX + 8}px` : tablet ? `${svgPosX}px` : undefined,
          paddingRight: tablet ? '8px' : undefined,
        }}
      >
        {store.activeBucketType === BucketTypes.TOTAL && !tablet && (
          <Grid item xs={12}>
            <DevelopmentFilter initial={defaultFilter} handleSetFilter={setFilter} />
          </Grid>
        )}
        <Grid item xs={12} className={devClasses.percentageSwitch}>
          <TextSwitch
            id={`percentage`}
            checked={percentage}
            afterChange={handlePercentageSwitch}
            options={[
              { label: t(`accountDetail_graph_abs_${currency}`) },
              { label: t('accountDetail_graph_percentage'), info: t('tooltip_accountDetail_graph_percentage') },
            ]}
          />
        </Grid>

        {currentProfit !== undefined && percentage && (
          <Grid container item xs={12} spacing={0} className={devClasses.info}>
            <Grid item xs={12} className="value profit">
              <span style={{ fontSize: '12px', fontWeight: 900 }}>{t('accountDetail_graph_portfolioProfit')}</span>
              <strong className={currentProfit >= 0 ? 'positive' : 'negative'}>
                {formatNumber(currentProfit, 2)} %
              </strong>
              <br />(
              {t(
                period === ChartPeriods.CUSTOM
                  ? 'accountDetail_graph_custom'
                  : periods.find((p) => p.value === period)?.label || '',
              ).toLowerCase()}
              )
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default React.memo(DevelopmentLineGraph);
